import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { statusColor } from './tileColors'
import { ArrowRight } from '@mui/icons-material'

const scoreStatus = (healthScore) => {
  if (healthScore === null || healthScore === undefined) return 'neutral'
  if (healthScore >= 80) return 'success'
  if (healthScore >= 60) return 'warning'
  return 'error'
}

const headlineValue = (kpi) => {
  if (kpi.type === 'gauge')
    return kpi.na ? 'N/A' : `${Math.round(kpi.percent)}%`
  if (kpi.format === 'minutes') return `${kpi.value}m`
  if (kpi.format === 'currency') return `£${Number(kpi.value).toLocaleString()}`
  if (kpi.format === 'percent') return `${kpi.value}%`
  return Number(kpi.value ?? 0).toLocaleString()
}

const SectionScoreCard = ({ section, summary, search }) => {
  const navigate = useNavigate()
  const Icon = section.icon
  const colors = statusColor(scoreStatus(summary?.healthScore))

  return (
    <Card sx={{ height: '100%', borderRadius: 3 }}>
      <CardActionArea
        onClick={() => navigate(`/kpi/${section.slug}${search}`)}
        sx={{
          height: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${section.color}18`,
            }}
          >
            <Icon sx={{ color: section.color }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography fontWeight={700} noWrap>
              {section.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
            >
              {section.blurb}
            </Typography>
          </Box>
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              value={summary?.healthScore ?? 0}
              size={52}
              thickness={4.5}
              sx={{ color: colors.main }}
            />
            <Box
              sx={{
                inset: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: colors.main }}
              >
                {summary?.healthScore === null ||
                summary?.healthScore === undefined
                  ? '—'
                  : `${summary.healthScore}`}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" justifyContent="space-between" gap={1}>
          {(summary?.headline ?? []).slice(0, 3).map((kpi) => (
            <Box key={kpi.key} textAlign="center" flex={1} minWidth={0}>
              <Typography fontWeight={700} sx={{ color: colors.main }}>
                {headlineValue(kpi)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.2 }}
              >
                {kpi.label}
              </Typography>
            </Box>
          ))}
          {!summary?.headline?.length && (
            <Typography variant="caption" color="text.secondary">
              {summary?.error
                ? 'Unable to load this section.'
                : 'No data in the selected range.'}
            </Typography>
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-end' }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 'auto', pt: 1 }}
          >
            {summary?.rated
              ? `${summary.onTarget}/${summary.rated} KPIs on target`
              : 'No rated KPIs in range'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 'auto', pt: 1 }}
          >
            View More {'>'}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  )
}

export default SectionScoreCard
